import { Component, afterNextRender, inject, input, output, signal, OnDestroy } from '@angular/core';

import {
  RyftControllerResponse,
  RyftCardFormComponentResponse,
  RyftFieldCollectionFormComponentResponse,
  RyftGooglePayComponentResponse,
  RyftApplePayComponentResponse,
  RyftPaymentAttemptResponse,
  RyftCardFormTheme,
  RyftFieldCollectionFormTheme,
} from '../../models';
import { RYFT_APPLE_PAY_CONFIG, RYFT_GOOGLE_PAY_CONFIG, RYFT_PUBLIC_KEY } from '../../providers';
import { RyftSdkService } from '../../services';

const RYFT_CARD_FORM_THEME: RyftCardFormTheme = {
  inputBackgroundColor: 'rgba(255, 255, 255, 0.1)',
  inputBorderColor: '#d7d7d7',
  inputFocusColor: '#ff0f95',
  inputTextColor: '#ffffff',
};

const RYFT_FIELD_COLLECTION_THEME: RyftFieldCollectionFormTheme = {
  ...RYFT_CARD_FORM_THEME,
  fieldCollectionFormDivisionGap: '0',
  fieldCollectionFormGap: '0.5rem',
};

@Component({
  selector: 'app-ryft-payment',
  templateUrl: './ryft-payment.component.html',
  styleUrls: ['./ryft-payment.component.scss'],
  standalone: true,
})
export class RyftPaymentComponent implements OnDestroy {
  protected pk = inject(RYFT_PUBLIC_KEY);
  protected googlePay = inject(RYFT_GOOGLE_PAY_CONFIG);
  protected applePay = inject(RYFT_APPLE_PAY_CONFIG);
  protected error = signal<string | null>(null);
  protected cardValid = signal<boolean>(false);
  protected addressValid = signal<boolean>(false);
  protected loading = signal<boolean>(false);

  secret = input.required<string>();
  complete = output();

  #ryftSdk = inject(RyftSdkService);
  #controller: RyftControllerResponse | null = null;
  #cardForm: RyftCardFormComponentResponse | null = null;
  #fieldCollectionForm: RyftFieldCollectionFormComponentResponse | null = null;
  #googlePayComponent: RyftGooglePayComponentResponse | null = null;
  #applePayComponent: RyftApplePayComponentResponse | null = null;

  constructor() {
    afterNextRender(() => {
      const controller = this.#ryftSdk.createController({
        publicKey: this.pk,
        clientSecret: this.secret(),
      });
      this.#controller = controller;

      const cardForm = this.#ryftSdk.createCardForm(controller, {
        paymentFieldConfig: {
          collectNameOnCard: true,
        },
        theme: RYFT_CARD_FORM_THEME,
      });
      this.#cardForm = cardForm;
      cardForm.mount('#ryft-card-form');

      const fieldCollectionForm = this.#ryftSdk.createFieldCollectionForm(controller, {
        fieldCollection: {
          customerAddress: {
            displayMode: 'minimum',
          },
        },
        theme: RYFT_FIELD_COLLECTION_THEME,
      });
      this.#fieldCollectionForm = fieldCollectionForm;
      fieldCollectionForm.mount('#ryft-field-collection');

      if (this.googlePay) {
        const googlePayComponent = this.#ryftSdk.createGooglePay(controller, this.googlePay);
        googlePayComponent.mount('#ryft-google-pay');
        this.#googlePayComponent = googlePayComponent;
      }

      if (this.applePay) {
        const applePayComponent = this.#ryftSdk.createApplePay(controller, this.applePay);
        applePayComponent.mount('#ryft-apple-pay');
        this.#applePayComponent = applePayComponent;
      }

      cardForm.on('validationChange', (ev) => {
        this.cardValid.set(ev.isValid);
        this.error.set(null);
      });

      fieldCollectionForm.on('validationChange', (ev) => {
        this.addressValid.set(ev.isValid);
        this.error.set(null);
      });

      controller.on('paymentSessionResult', (ev) => {
        this.loading.set(true);
        this.error.set(null);
        this.handlePaymentResult(ev.result);
      });
    });
  }

  ngOnDestroy() {
    this.#googlePayComponent?.unmount();
    this.#applePayComponent?.unmount();
    this.#controller?.destroy();
  }

  protected submit(ev: Event) {
    ev.preventDefault();

    if (!this.#cardForm) return;

    this.loading.set(true);

    this.#cardForm
      .attemptPayment({ clientSecret: this.secret() })
      .then((result) => this.handlePaymentResult(result))
      .catch((error) => {
        this.error.set(error?.message ?? String(error));
        this.loading.set(false);
      });
  }

  protected handlePaymentResult(result: RyftPaymentAttemptResponse) {
    if (result.type === 'action-required') {
      // 3DS or other action required - SDK handles this automatically
      return;
    }

    const session = result.paymentSession;
    if (session.status && ['Approved', 'Captured'].includes(session.status)) {
      this.loading.set(false);
      this.complete.emit();
      return;
    }

    if (result.userFacingErrorMessage) {
      this.error.set(result.userFacingErrorMessage);
    }

    this.loading.set(false);
  }
}
